import React from 'react';
import SEO from '../components/SEO';
import PageShell from '../components/site/PageShell';
import { brand } from '../config/esanzaContent';

const sections = [
  {
    title: 'Information we collect',
    body: [
      'Account information such as name, email address, phone number, role, organization, and login activity.',
      'Field records such as farmer profiles, cohort details, crop and tree traceability records, observations, issue reports, photos, and GPS-enabled location data when field teams choose to capture it.',
      'Technical information such as device type, app activity, timestamps, and security logs needed to operate and protect the platform.',
    ],
  },
  {
    title: 'How we use information',
    body: [
      'To provide staff login, farmer onboarding, field scouting, traceability, issue reporting, training, VSLA, and operational dashboard features.',
      'To support internal reporting, quality control, demo readiness, field validation, and platform security.',
      'To communicate with authorized users about access, support requests, service updates, and account administration.',
    ],
  },
  {
    title: 'Location, photos, and field data',
    body: [
      'The eSanza mobile app may request location and camera access for field operations, including mapping farms, recording tree observations, attaching issue photos, and improving traceability records.',
      'These permissions are used only for app functionality and are controlled by the user and the operating system.',
    ],
  },
  {
    title: 'Sharing and storage',
    body: [
      'We do not sell personal data. Data is used for eSanza platform operations and may be processed by trusted hosting, database, email, analytics, or infrastructure providers that help us run the service.',
      'Operational records are stored in secure cloud systems and protected using access controls appropriate to the role of each authorized user.',
    ],
  },
  {
    title: 'Security',
    body: [
      'We use HTTPS, authenticated access, role-based permissions, audit logs, and administrative controls to protect platform data.',
      'No digital system is completely risk-free, but we work to keep the platform secure and to limit access to authorized staff and approved users.',
    ],
  },
  {
    title: 'Your choices and deletion requests',
    body: [
      'Authorized users may request correction, export, or deletion of personal account data where legally and operationally possible.',
      `To request support or data deletion, contact ${brand.email}. Some records may be retained when required for security, legal compliance, audit, or legitimate operational reporting.`,
    ],
  },
];

const PrivacyPolicyPage: React.FC = () => (
  <>
    <SEO
      title="Privacy Policy | eSanza"
      description="Privacy policy for eSanza, a staff and field operations platform for agricultural traceability, farmer records, and internal project workflows."
    />
    <PageShell tone="light">
      <section className="relative overflow-hidden bg-[#06150e] px-5 pb-16 pt-32 text-white lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(212,175,55,0.22),transparent_34%),linear-gradient(135deg,#06150e_0%,#0c2a1b_100%)]" />
        <div className="relative mx-auto max-w-5xl">
          <p className="text-xs font-extrabold uppercase tracking-[0.28em] text-[#d4af37]">Privacy Policy</p>
          <h1 className="mt-5 text-4xl font-black tracking-tight md:text-6xl">How eSanza protects platform data.</h1>
          <p className="mt-6 max-w-3xl text-base leading-8 text-white/72">
            This policy explains how {brand.company} collects, uses, stores, and protects information through the eSanza website, dashboard, and mobile app.
          </p>
          <p className="mt-5 text-sm font-semibold text-white/55">Last updated: July 7, 2026</p>
        </div>
      </section>

      <section className="px-5 py-16 lg:px-8">
        <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[0.75fr_1.25fr]">
          <aside className="h-fit rounded-lg border border-[#d9dfd0] bg-white p-6 shadow-sm">
            <img src={brand.icon} alt="eSanza" className="h-14 w-14 rounded-xl" />
            <h2 className="mt-5 text-xl font-black text-[#102016]">{brand.company}</h2>
            <p className="mt-3 text-sm leading-6 text-[#5d6b61]">
              Registered in Rwanda. eSanza is used for agricultural operations, field records, traceability, and internal platform workflows.
            </p>
            <div className="mt-6 rounded-lg bg-[#f1f4ec] p-4 text-sm text-[#526057]">
              <p className="font-black text-[#102016]">Privacy contact</p>
              <a href={`mailto:${brand.email}`} className="mt-2 block font-semibold text-[#0f7a4d] hover:text-[#987a1f]">
                {brand.email}
              </a>
            </div>
          </aside>

          <div className="space-y-5">
            {sections.map((section) => (
              <article key={section.title} className="rounded-lg border border-[#d9dfd0] bg-white p-6 shadow-sm">
                <h2 className="text-2xl font-black text-[#102016]">{section.title}</h2>
                <div className="mt-4 space-y-3">
                  {section.body.map((paragraph) => (
                    <p key={paragraph} className="text-sm leading-7 text-[#5d6b61]">{paragraph}</p>
                  ))}
                </div>
              </article>
            ))}

            <article className="rounded-lg border border-[#d9dfd0] bg-[#102016] p-6 text-white shadow-sm">
              <h2 className="text-2xl font-black">Contact us</h2>
              <p className="mt-4 text-sm leading-7 text-white/70">
                For privacy questions, access requests, or deletion requests, contact {brand.company} at{' '}
                <a href={`mailto:${brand.email}`} className="font-bold text-[#d4af37]">{brand.email}</a>.
              </p>
            </article>
          </div>
        </div>
      </section>
    </PageShell>
  </>
);

export default PrivacyPolicyPage;
